import { useState, useEffect, useCallback } from 'react'
import { authAPI, tokenStorage } from '../services/api'
import { AuthContext } from './auth-context'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a refresh token
        const refreshToken = tokenStorage.getRefreshToken()
        if (refreshToken) {
          // Try to get current user (this will auto-refresh token if needed)
          try {
            const userData = await authAPI.getCurrentUser()
            setUser(userData)
          } catch (error) {
            // Token expired or invalid, clear tokens
            // Don't log errors here as backend might not be running
            tokenStorage.clearTokens()
            setUser(null)
          }
        } else {
          // No refresh token, user is not logged in
          setUser(null)
        }
      } catch (error) {
        // Network error or other issue - just clear and continue
        console.error('Auth check error:', error)
        tokenStorage.clearTokens()
        setUser(null)
      } finally {
        // Always set loading to false, even if there's an error
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (email, password) => {
    try {
      const data = await authAPI.login(email, password)
      setUser(data.user)
      return { success: true, user: data.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const signup = async (email, username, password, policyAgreed = false) => {
    try {
      const data = await authAPI.signup(email, username, password, policyAgreed)
      setUser(data.user)
      return { success: true, user: data.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      tokenStorage.clearTokens()
    }
  }

  const refreshUser = useCallback(async () => {
    if (!tokenStorage.getRefreshToken()) {
      return
    }
    try {
      const userData = await authAPI.getCurrentUser()
      setUser(userData)
    } catch (error) {
      console.error('refreshUser:', error)
    }
  }, [])

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    signup,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
